import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

const API_URL = `${window.location.origin}/api`;

const AgentAdminsManagement = () => {
  const { currentUser } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  
  const [selectedAgency, setSelectedAgency] = useState('');
  const [selectedUser, setSelectedUser] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Load agencies and users in parallel
      const [agenciesRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/agencies`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }),
        axios.get(`${API_URL}/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
      ]);
      
      setAgencies(agenciesRes.data);
      setUsers(usersRes.data);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load agencies and users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
    toast.info('Refreshing agent admins data...');
  };

  const assignAgentAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    
    if (!selectedAgency || !selectedUser) {
      setError('Please select both an agency and a user');
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/agencies/${selectedAgency}/agent-admins`, 
        { userId: selectedUser },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setSuccessMessage('Agent admin assigned successfully');
      
      // Reset selections
      setSelectedAgency('');
      setSelectedUser('');
      
      // Refresh data
      fetchData();
    } catch (err) {
      console.error('Error assigning agent admin:', err);
      setError(err.response?.data?.message || 'Failed to assign agent admin');
    }
  };

  const removeAgentAdmin = async (agencyId, userId) => {
    if (!window.confirm('Are you sure you want to remove this agent admin?')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/agencies/${agencyId}/agent-admins/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setSuccessMessage('Agent admin removed successfully');
      
      // Refresh data
      fetchData();
      
      // Clear success message after a delay
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error removing agent admin:', err);
      setError(err.response?.data?.message || 'Failed to remove agent admin');
    }
  };

  // Get non-admin users (candidates for agent admin)
  const getNonAdminUsers = () => {
    if (!Array.isArray(users)) return [];
    return users.filter(user => 
      user.role === 'user' && 
      (!user.agencyId || user.agencyStatus !== 'approved')
    );
  };

  // Get all agent admins
  const getAgentAdmins = () => {
    if (!Array.isArray(users)) return [];
    return users.filter(user => user.role === 'agent_admin');
  };

  // Get agency name by ID
  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a._id === agencyId);
    return agency ? agency.name : 'Unknown Agency';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="agent-admins-management">
      <div className="controls-row">
        <div className="title-section">
          <h3>Agent Admins Management</h3>
          {lastRefresh && (
            <div className="last-refresh">
              Last refreshed: {lastRefresh}
            </div>
          )}
        </div>
        
        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Data
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <div className="admin-section">
        <h3>Assign New Agent Admin</h3>
        <form onSubmit={assignAgentAdmin} className="assign-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="agency">Select Agency *</label>
              <select
                id="agency"
                value={selectedAgency}
                onChange={(e) => setSelectedAgency(e.target.value)}
                required
              >
                <option value="">Select an agency</option>
                {agencies.map(agency => (
                  <option key={agency._id} value={agency._id}>
                    {agency.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="user">Select User *</label>
              <select
                id="user"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                required
              >
                <option value="">Select a user</option>
                {getNonAdminUsers().map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <button type="submit" className="btn-primary">
            Assign as Agent Admin
          </button>
        </form>
      </div>
      
      <div className="admin-section">
        <h3>Existing Agent Admins</h3>
        {getAgentAdmins().length === 0 ? (
          <p>No agent admins found.</p>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Agency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getAgentAdmins().map(admin => (
                  <tr key={admin._id}>
                    <td>{admin.name}</td>
                    <td>{admin.email}</td>
                    <td>{getAgencyName(admin.agencyId)}</td>
                    <td>
                      <button
                        className="btn-secondary"
                        onClick={() => removeAgentAdmin(admin.agencyId, admin._id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAdminsManagement; 